import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import SiteSelector from './SiteSelector';
import { getOrders } from '../services/api';
import { MATERIAL_NAME, MATERIAL_UNIT, type Site, type SourcingRow } from '../types';
import { invClass, money, miles, num } from '../utils/format';
import { useTableSort } from '../hooks/useTableSort';
import type { OrderRequest } from './HomeMap';
import InfoTip from './InfoTip';
import { TIPS } from '../tour/copy';

export default function CheapestYardPanel({
  sites,
  material,
  siteId,
  onSelectSite,
  rows,
  loading,
  selectedYardId,
  onHighlightYard,
  onRequestOrder,
}: {
  sites: Site[];
  material: string;
  siteId: string | null;
  onSelectSite: (siteId: string | null) => void;
  rows: SourcingRow[];
  loading: boolean;
  selectedYardId: string | null;
  onHighlightYard: (yardId: string) => void;
  onRequestOrder: (req: OrderRequest) => void;
}) {
  const site = sites.find((s) => s.site_id === siteId) ?? null;
  const unit = MATERIAL_UNIT[material] ?? 'unit';
  const [needed, setNeeded] = useState<number>(20);
  const { onSort, sortRows, indicator } = useTableSort<SourcingRow>();

  // Total quantity of OPEN orders for the selected site + material, if any.
  const ordersQ = useQuery({ queryKey: ['orders'], queryFn: () => getOrders(500) });
  const openNeeded = useMemo(() => {
    if (!siteId) return null;
    const sum = (ordersQ.data?.orders ?? [])
      .filter((o) => o.status === 'open' && o.site_id === siteId && o.material === material)
      .reduce((s, o) => s + Number(o.quantity || 0), 0);
    return sum > 0 ? sum : null;
  }, [ordersQ.data, siteId, material]);

  // Auto-populate Needed from open orders when the selection changes; otherwise
  // fall back to the default. (Manual edits stick until the selection/orders change.)
  useEffect(() => {
    setNeeded(openNeeded ?? 20);
  }, [siteId, material, openNeeded]);

  // Cheapest ELIGIBLE yard (rows arrive sorted by delivered price from the server).
  const cheapestEligibleId = useMemo(
    () => rows.find((r) => r.delivered_price != null && (r.inventory ?? 0) >= needed)?.yard_id ?? null,
    [rows, needed]
  );

  const displayRows = sortRows(rows, {
    yard: (r) => r.yard_name ?? r.yard_id,
    material: (r) => r.material_price,
    dist: (r) => r.distance_miles,
    freight: (r) => r.freight_cost,
    inv: (r) => r.inventory,
    delivered: (r) => r.delivered_price,
  });

  const placeOrder = (r: SourcingRow, eligible: boolean, stock: number) => {
    if (!eligible) {
      const ok = window.confirm(
        `${r.yard_name ?? r.yard_id} has only ${num(stock)} ${unit} of ${
          MATERIAL_NAME[material] ?? material
        } on hand, but the job needs ${num(needed)} ${unit}. Place the order anyway?`
      );
      if (!ok) return;
    }
    onRequestOrder({
      siteId: site?.site_id ?? null,
      siteName: site?.name ?? undefined,
      siteAddress:
        site?.address || [site?.city, site?.state].filter(Boolean).join(', ') || site?.site_id || '',
      yardId: r.yard_id,
      deliveredPerUnit: r.delivered_price!,
      quantity: needed,
    });
  };

  return (
    <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10, height: '100%' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <SiteSelector sites={sites} value={siteId} onChange={onSelectSite} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <label className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            Needed ({unit})
          </label>
          <input
            className="input"
            type="number"
            min={0}
            step="any"
            value={needed}
            onChange={(e) => setNeeded(Math.max(0, Number(e.target.value)))}
            style={{ width: 72, textAlign: 'right' }}
          />
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {!siteId && <p className="muted">Pick a site to rank yards by delivered price.</p>}
        {siteId && loading && <p className="muted">Loading…</p>}
        {siteId && !loading && rows.length === 0 && (
          <p className="muted">No priced yards for this site + material.</p>
        )}
        {siteId && rows.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => onSort('yard')}>
                  Yard{indicator('yard')}
                </th>
                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => onSort('material')}>
                  Material ${indicator('material')}
                  <InfoTip content={TIPS.colMaterialPrice} />
                </th>
                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => onSort('dist')}>
                  Dist{indicator('dist')}
                  <InfoTip content={TIPS.colDist} />
                </th>
                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => onSort('freight')}>
                  Freight ${indicator('freight')}
                  <InfoTip content={TIPS.colFreight} />
                </th>
                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => onSort('inv')}>
                  INV{indicator('inv')}
                  <InfoTip content={TIPS.colInv} />
                </th>
                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => onSort('delivered')}>
                  Delivered $/{unit}
                  {indicator('delivered')}
                  <InfoTip content={TIPS.colDelivered} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r) => {
                const stock = r.inventory ?? 0;
                const eligible = r.delivered_price != null && stock >= needed;
                const isCheapest = r.yard_id === cheapestEligibleId;
                return (
                  <tr
                    key={r.yard_id}
                    className={selectedYardId === r.yard_id ? 'row-selected' : ''}
                    style={{ cursor: 'pointer', opacity: eligible ? 1 : 0.7 }}
                    onClick={() => onHighlightYard(r.yard_id)}
                  >
                    <td>
                      <span style={{ whiteSpace: 'nowrap' }}>{r.yard_name ?? r.yard_id}</span>{' '}
                      {isCheapest && (
                        <>
                          <span className="badge badge-cheapest">Cheapest</span>
                          <InfoTip content={TIPS.cheapestBadge} />
                        </>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{money(r.material_price)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{miles(r.distance_miles)}</td>
                    <td style={{ textAlign: 'right' }}>{money(r.freight_cost)}</td>
                    <td
                      className={invClass(stock, needed)}
                      style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                    >
                      {num(stock)} {unit}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(r.delivered_price)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {r.delivered_price != null && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            placeOrder(r, eligible, stock);
                          }}
                        >
                          Order
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
